"use client";

import { GizmoHelper, GizmoViewport, useProgress } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { Bloom, EffectComposer } from "@react-three/postprocessing";
import { getProject, onChange } from "@theatre/core";
import { editable as e, PerspectiveCamera, SheetProvider } from "@theatre/r3f";
import dynamic from "next/dynamic";
import { KernelSize, Resolution } from "postprocessing";
// import { Perf } from "r3f-perf";
import { use, useEffect, useRef, useState } from "react";
import { type Group, Vector3 } from "three";
import { getDistanceFromTop } from "@/lib/utils";
import { HeroContext } from "@/providers/hero-provider";
import { ScrollLockContext } from "@/providers/scroll-lock-provider";
import theatreState from "./piperunner_website.theatre-project-state.json";

/**
 * Dynamic import is needed for GLTF loader to work, else
 * build process breaks
 *
 * https://discourse.threejs.org/t/nextjs-and-threejs-fiber-url-problem/57346/6
 */
const Statue = dynamic(() => import("./Statue"), {
  ssr: false,
});

/**
 * Use this variable to test the theatre project in development
 * without having to deploy the project.
 */
const THEATRE_TEST_MODE = true;
const finalPosition = new Vector3(0, -3.85, -3.07);
const startPosition = new Vector3(0, -4.91, -3.07);
const MAX_DISTANCE = startPosition.distanceTo(finalPosition);
const finalTimeStep = 3;

const sheet =
  process.env.NODE_ENV === "production" || THEATRE_TEST_MODE
    ? getProject("piperunner_website", {
        state: theatreState,
      }).sheet("home_page")
    : getProject("piperunner_website").sheet("home_page");

const isProduction = process.env.NODE_ENV === "production" || THEATRE_TEST_MODE;

export default function Scene() {
  const { lockScroll, unlockScroll } = use(ScrollLockContext);

  const statueRef = useRef<Group>(null);

  const { isLoadingAnimationFinished } = use(HeroContext);
  /** Since we know isLoadingAnimationFinished will start with true,
   * only if user has already visited the page, we can set the scroll lock
   * value using that.
   */
  const [isScrollLocked, setIsScrollLocked] = useState(!isLoadingAnimationFinished);

  const { progress, errors } = useProgress();

  useEffect(
    () => {
      if (isScrollLocked) {
        // scroll to top
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        lockScroll("home_page", 1);
      } else {
        unlockScroll("home_page", 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isScrollLocked],
  );

  /**
   * A number between 0 and 1, 0 being top of the page and 1 being top of About page.
   */
  const scrollPosition = useRef<number | null>(null);

  useEffect(() => {
    const aboutFromTop = getDistanceFromTop(document.getElementById("activity") as HTMLElement);

    const handler = () => {
      scrollPosition.current = window.scrollY / aboutFromTop;
    };

    // Running it once to get the initial scroll position
    handler();
    window.addEventListener("scroll", handler);

    return () => {
      window.removeEventListener("scroll", handler);
    };
  }, []);

  /**
   * Initialize Theatre Studio in development
   */
  useEffect(() => {
    if (!isProduction) {
      (async () => {
        // * INFO: You can show or hide Studio by pressing Alt + \.
        const studio = (await import("@theatre/studio")).default;
        const extension = (await import("@theatre/r3f/dist/extension")).default;
        studio.initialize();
        studio.extend(extension);
      })();
    }
  }, []);

  /**
   * Play the sequence when the loading progress is 100
   * and the loading animation is finished
   *
   * Note: This hook is used in production
   */
  useEffect(() => {
    if (progress === 100 && isProduction && isLoadingAnimationFinished) {
      (async () => {
        await sheet.project.ready;
        sheet.sequence.play({
          iterationCount: 1,
          range: [0, finalTimeStep],
        });

        onChange(sheet.sequence.pointer.position, (len) => {
          if (len === finalTimeStep) {
            setIsScrollLocked(false);
          }
        });
      })();
    }

    /**
     * If there is a loading error, we unlock scroll
     */
    if (isLoadingAnimationFinished && errors?.length > 0) {
      setIsScrollLocked(false);
    }
  }, [progress, isLoadingAnimationFinished, errors]);

  /**
   * Move the statue based on scroll position
   *
   * Note: This is blocked until the sequence is
   * finished playing and the loading progress is 100
   */
  useFrame((_, delta) => {
    if (sheet.sequence.position < finalTimeStep || progress < 100) return;

    if (statueRef.current && scrollPosition.current !== null) {
      const newPosition = new Vector3();
      newPosition.lerpVectors(startPosition, finalPosition, scrollPosition.current);

      /** alpha is capped between (0, 1), closer the vectors, higher the value */
      const alpha = Math.min(
        Math.max(Math.abs((MAX_DISTANCE - statueRef.current.position.distanceTo(newPosition)) / MAX_DISTANCE), 0.1),
        0.9,
      );

      statueRef.current.position.lerp(newPosition, alpha);
    }
  });

  return (
    <SheetProvider sheet={sheet}>
      <PerspectiveCamera theatreKey="Camera" makeDefault position={[5, 5, -5]} fov={75} />

      <ambientLight intensity={Math.PI / 2} />
      <e.spotLight
        theatreKey="Spot Light"
        position={[10, 10, 10]}
        angle={0.15}
        penumbra={1}
        decay={0}
        intensity={Math.PI}
      />
      <e.pointLight theatreKey="Point Light" position={[-10, -10, -10]} decay={0} intensity={Math.PI} />

      {/* <fog attach="fog" args={["#202030", 10, 25]} /> */}
      <e.group ref={statueRef} theatreKey="Statue">
        <Statue />
      </e.group>

      <EffectComposer>
        <Bloom
          intensity={1.0} // The bloom intensity.
          blurPass={undefined} // A blur pass.
          kernelSize={KernelSize.LARGE} // blur kernel size
          luminanceThreshold={0.9} // luminance threshold. Raise this value to mask out darker elements in the scene.
          luminanceSmoothing={0.025} // smoothness of the luminance threshold. Range is [0, 1]
          mipmapBlur={false} // Enables or disables mipmap blur.
          resolutionX={Resolution.AUTO_SIZE} // The horizontal resolution.
          resolutionY={Resolution.AUTO_SIZE} // The vertical resolution.
        />
      </EffectComposer>

      {!isProduction && (
        <GizmoHelper
          alignment="bottom-right" // widget alignment within scene
          margin={[80, 80]} // widget margins (X, Y)
        >
          <GizmoViewport axisColors={["red", "green", "blue"]} labelColor="black" />
          {/* <Perf position="bottom-right" /> */}
        </GizmoHelper>
      )}
    </SheetProvider>
  );
}
